export const IS_EASTER_ENABLED = false;

/**
 * End of Summer Sale 2026, ran 28 Aug – 6 Sep 2026 (WEB-1455). Over (WEB-1500).
 *
 * `false` redirects `/end-of-summer-sale-2026` to the home page rather than
 * 404ing it — the campaign URL went out in newsletters and ads, and those keep
 * being clicked for weeks after the sale ends. It also hands the homepage
 * carousel slot back to Hot Deals and suppresses every campaign badge, whatever
 * is still tagged in Strapi.
 *
 * The bottles' discounted asks are not tied to this flag and do not expire with
 * the campaign — an ask lives 30 days from creation, so the sale asks run into
 * late September. Turning the campaign off removes the framing, not the prices.
 */
export const IS_END_OF_SUMMER_SALE_ENABLED = false;

/**
 * Strapi `landingpage` slug of the campaign, without the locale suffix that
 * every landing-page lookup appends itself (`…-en`, `…-de`, …).
 */
export const END_OF_SUMMER_SALE_STRAPI_SLUG = 'end-of-summer-sale-2026';

/**
 * Route of the campaign page.
 *
 * Named because the campaign badge is triggered by the path, the way the Black
 * Friday and Easter ones are — and those repeat their route as a string literal
 * in every component that has to recognise it.
 */
export const END_OF_SUMMER_SALE_PATH = '/end-of-summer-sale-2026';

/**
 * Query flag that carries the campaign from a card to the bottle page, so the
 * badge and the struck-through retail price survive the click (`?bf=1` and
 * `?easter=1` do the same for the other two).
 */
export const END_OF_SUMMER_SALE_QUERY_FLAG = 'eos';

/**
 * Value marketing puts in the Strapi `sku_tags` JSON to mark a bottle as part
 * of the campaign: `{ "tags": ["summersale"] }`.
 *
 * The route alone only badges bottles while a visitor is standing on the
 * campaign page. The tag travels with the bottle, so a sale bottle is also
 * marked on the home page, in search results and on brand pages — and it puts
 * the selection in marketing's hands instead of requiring a deploy.
 *
 * Strapi validates nothing here, so a typo is silent: the field is free-form
 * JSON and the string is a convention, the way `preorder` and `hot_deal` are.
 *
 * Untagging 49 bottles at the end of the sale is not the safety net —
 * `IS_END_OF_SUMMER_SALE_ENABLED` is. Flipping that suppresses every campaign
 * badge at once, whatever is still tagged in Strapi.
 */
export const END_OF_SUMMER_SALE_SKU_TAG = 'summersale';

/**
 * Search-result copy for the campaign page, written by marketing.
 *
 * Lives here rather than in Strapi because the `landingpage` content type has
 * no SEO fields — the same reason the shop pages keep theirs in
 * `retail-stores.constants.ts` (WEB-1357). Until it does, every revision needs
 * a deploy.
 *
 * Titles stay at or below 65 characters and descriptions below 155: Google cuts
 * both off around there, and the tail of these sentences carries the discount
 * and the dates.
 */
export const END_OF_SUMMER_SALE_META = new Map<string, { title: string; description: string }>([
  [
    'en',
    {
      title: 'End of Summer Sale 2026: Up to 58% Off. Best Whisky Deals Europe.',
      description:
        "Shop Spiritory's End of Summer Sale: 70 whisky deals from 24 brands like Macallan, Bowmore and Redbreast, up to 58% off. Aug 28-Sept 6.",
    },
  ],
  [
    'de',
    {
      title: 'Spätsommer Sale 2026: Bis zu 58% Rabatt. Whisky-Deals Europa.',
      description:
        'Entdecke Spiritorys Sommerabverkauf: 70 Whisky-Angebote von 24 Marken wie Macallan, Bowmore und Redbreast, bis zu 58% Rabatt. 28. Aug.-6. Sep.',
    },
  ],
  [
    'fr',
    {
      title: "Soldes de Fin d'Été 2026 : Jusqu'à 58%. Meilleures Offres Europe.",
      description:
        "Découvrez les Soldes de Fin d'Été de Spiritory : 70 offres whisky de 24 marques comme Macallan, Bowmore et Redbreast, jusqu'à 58%. Du 28 août au 6 sept.",
    },
  ],
  [
    'it',
    {
      title: 'Saldi di Fine Estate 2026: Fino al 58%. Offerte Whisky Europa.',
      description:
        'Scopri i Saldi di Fine Estate di Spiritory: 70 offerte whisky da 24 marchi come Macallan, Bowmore e Redbreast, fino al 58% di sconto. 28 ago-6 set.',
    },
  ],
  [
    'es',
    {
      title: 'Rebajas de Fin de Verano 2026: Hasta 58% Dto. Whisky en Europa.',
      description:
        'Compra las Rebajas de Fin de Verano de Spiritory: 70 ofertas de whisky de 24 marcas como Macallan, Bowmore y Redbreast, hasta 58% dto. 28 ago-6 sep.',
    },
  ],
]);

/** Campaign SEO copy for a language, falling back to English. */
export const getEndOfSummerSaleMeta = (locale: string | undefined) =>
  END_OF_SUMMER_SALE_META.get((locale || 'en').toLowerCase()) ?? END_OF_SUMMER_SALE_META.get('en')!;
